// src/components/store/sections/weekly-banner/DynamicWeeklyBannerVariant.tsx
"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Flame, Sparkles, Tag, Ticket, Zap } from "lucide-react";

export default function DynamicWeeklyBannerVariant({
  variantNumber,
  archKey,
  archLabel,
  palName,
  grad,
  lightBg,
  textCol,
  borderCol,
  banner,
  subdomain,
}: any) {
  const imageUrl = banner?.image
    ? banner.image.startsWith("http") || banner.image.startsWith("data:")
      ? banner.image
      : `http://${subdomain ? `${subdomain}.` : ""}localhost:8000${banner.image.startsWith("/") ? "" : "/"}${banner.image}`
    : "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=800&auto=format&fit=crop&q=80";

  // Ticket Coupon Tear-Off Banner
  if (archKey === "ticket_coupon") {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 select-none font-sans">
        <div className={`bg-white rounded-3xl border-2 ${borderCol} shadow-xl overflow-hidden grid grid-cols-1 md:grid-cols-12`}>
          <div className={`md:col-span-8 p-6 sm:p-9 text-left space-y-3 ${lightBg}`}>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-gradient-to-r ${grad} text-white text-[10px] font-mono font-black uppercase`}>
              <Ticket className="w-3.5 h-3.5" /> COUPON #{variantNumber} • {palName}
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 leading-tight">
              {banner?.title || `${archLabel} — Save 40% This Week`}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-lg">
              {banner?.subtitle || "Use this weekly pass at checkout for instant storewide savings."}
            </p>
            <Link href={banner?.target_url || "/products"} className={`px-6 py-2.5 rounded-xl bg-gradient-to-r ${grad} text-white font-black text-xs uppercase inline-flex items-center gap-2 shadow-md`}>
              <span>{banner?.button_text || "Redeem Pass"}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className={`md:col-span-4 bg-gradient-to-br ${grad} p-6 flex items-center justify-center border-t-2 md:border-t-0 md:border-l-2 border-dashed border-white/60 relative min-h-[190px]`}>
            <Image src={imageUrl} alt="Weekly Deal" fill unoptimized className="object-contain p-4 drop-shadow-2xl" />
          </div>
        </div>
      </section>
    );
  }

  // Bento Split Promo Grid
  if (archKey === "bento_promo" || archKey === "clay_deck") {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 select-none font-sans">
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-3 p-3.5 rounded-[32px] ${lightBg} border-2 ${borderCol} shadow-lg`}>
          <div className="md:col-span-7 bg-white rounded-2xl p-6 text-left flex flex-col justify-between space-y-3 border border-slate-100">
            <span className={`text-[10px] font-mono font-black uppercase ${textCol}`}>
              BENTO PROMO #{variantNumber} • {palName}
            </span>
            <h2 className="text-xl sm:text-3xl font-black text-slate-900">
              {banner?.title || `${archLabel} Flash Event`}
            </h2>
            <p className="text-xs text-slate-500">{banner?.subtitle || "Curated weekly deals with express shipping."}</p>
            <div>
              <Link href={banner?.target_url || "/products"} className={`px-5 py-2.5 rounded-xl bg-gradient-to-r ${grad} text-white font-black text-xs uppercase inline-flex items-center gap-1.5`}>
                <span>{banner?.button_text || "Shop Bento Deal"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <div className={`md:col-span-5 relative rounded-2xl bg-gradient-to-br ${grad} min-h-[180px] flex items-center justify-center overflow-hidden p-4`}>
            <Image src={imageUrl} alt="Promo" fill unoptimized className="object-contain p-2 drop-shadow-xl" />
          </div>
        </div>
      </section>
    );
  }

  const isDark = archKey === "cyber_hologram" || archKey === "magazine_issue" || palName === "SlateGold";
  const stageShape =
    archKey === "cathedral_arch"
      ? "rounded-t-[80px] rounded-b-2xl"
      : archKey === "vinyl_portal"
      ? "rounded-full aspect-square"
      : archKey === "polaroid_board"
      ? "rounded-2xl bg-white p-3 -rotate-2"
      : "rounded-3xl";

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 select-none font-sans">
      <div
        className={`relative rounded-[36px] overflow-hidden border-2 ${borderCol} ${
          isDark ? "bg-slate-950 text-white" : `bg-gradient-to-r ${grad} text-white`
        } p-6 sm:p-10 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6`}
      >
        <div className="space-y-3 max-w-xl text-left relative z-10">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-white/95 text-slate-900 text-[10px] font-black uppercase tracking-widest shadow-xs">
            <Tag className="w-3.5 h-3.5 text-rose-500" />
            <span>{banner?.badge_title || `WEEKLY DEAL #${variantNumber} • ${archLabel.toUpperCase()}`}</span>
          </span>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight">
            {banner?.title || `${archLabel} (${palName}) — Flat 40% Off`}
          </h2>
          <p className="text-xs sm:text-sm text-white/85 leading-relaxed max-w-md">
            {banner?.subtitle || `Exclusive ${palName} edition weekly offer on our highest-rated collection.`}
          </p>
          <div className="pt-2">
            <Link
              href={banner?.target_url || "/products"}
              className="px-6 py-3 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-black text-xs uppercase tracking-wider inline-flex items-center gap-2 shadow-lg active:scale-95 transition-all"
            >
              <span>{banner?.button_text || "Claim Weekly Offer"}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className={`relative w-52 sm:w-64 h-44 sm:h-52 bg-white/15 backdrop-blur-md ${stageShape} border border-white/30 flex items-center justify-center p-4 shrink-0 shadow-inner`}>
          <Image src={imageUrl} alt="Weekly Deal" fill unoptimized className="object-contain p-3 drop-shadow-2xl" />
        </div>
      </div>
    </section>
  );
}
